import '../invoice.scss';

import isEmpty from 'lodash/isEmpty';
import { arrayOf, bool, func } from 'prop-types';
import React, { useState } from 'react';
import { GoTrashcan } from 'react-icons/go';
import Moment from 'react-moment';
import { Button, Col, Collapse, Row } from 'reactstrap';

import ExpandButton from '../../../generic/ExpandButton';
import SignedCurrencyFormat from '../../../generic/SignedCurrencyFormat';
import withConfirm from '../../../generic/with-confirm';
import RequestedChanges from '../../../invoices/RequestedChanges';
import SapInvoiceStatusBadge from '../../../invoices/SapInvoiceStatusBadge';
import StatusBadge from '../../../invoices/StatusBadge';
import { calculateTotalSum } from '../../../invoices/utils';
import { detailedInvoiceShape, productShape } from '../../../prop-types';
import { UI_SHORT_DATE_FORMAT } from '../../../utils/constants';
import ListBookingEntries from '../../booking-entries/ListBookingEntries';
import {
  hasSapFieldsValue,
  isInvoiceStatusExported,
  isInvoiceStatusReadOnly,
} from '../../utils';
import UpdateInvoiceModal from '../UpdateInvoice';

const DeleteWithConfirmation = withConfirm(
  'Do you really want to delete this invoice?',
)(Button);

function InvoiceLine({
  invoice,
  readOnly,
  products,
  onDelete,
  isOpenByDefault,
  isEditing,
}) {
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded(!expanded);
  return (
    <>
      <div
        id={`invoice-line-${invoice.id}`}
        className="c-balance-line c-invoice-line c-invoice-line__header"
      >
        <Row className="d-flex align-items-center">
          <Col sm={2} className="d-flex">
            {!isEmpty(invoice.bookingEntries) && (
              <ExpandButton
                data-qa-field="expand-invoice"
                expanded={expanded}
                onClick={toggle}
              />
            )}
            <div className="px-2">
              <UpdateInvoiceModal
                products={products}
                id={invoice.id}
                isOpenByDefault={isOpenByDefault}
                isEditing={isEditing}
              />
              <div className="small">
                {invoice.accountSfId && <span>{invoice.accountSfId}</span>}
              </div>
            </div>
          </Col>
          <Col sm={2}>
            {isInvoiceStatusExported(invoice.status) ? (
              <>
                <StatusBadge status={invoice.status} className="mr-1" />
                <SapInvoiceStatusBadge status={invoice.sapStatus} />
              </>
            ) : (
              <StatusBadge status={invoice.status} />
            )}
          </Col>
          <Col sm={1}>
            <div data-qa-field="inv-billingType">{invoice.billingType}</div>
            <div className="small text-muted">Billing Type</div>
          </Col>
          <Col sm={1}>
            <div data-qa-field="inv-createdBy">{invoice.createdBy}</div>
            <div className="small text-muted">Created by</div>
          </Col>
          <Col sm={3}>
            <>
              <Moment format={UI_SHORT_DATE_FORMAT}>{invoice.created}</Moment>
              <div className="small text-muted">Creation Date</div>
            </>
          </Col>
          <Col sm={1} data-qa-field="inv-totalSum" className="px-0">
            <SignedCurrencyFormat
              value={calculateTotalSum(invoice.bookingEntries)}
            />
            <div className="small text-muted">Invoice amount</div>
          </Col>
          <Col sm={1}>
            {!isInvoiceStatusReadOnly(invoice.status) &&
              !hasSapFieldsValue(invoice) &&
              isEditing && (
                <DeleteWithConfirmation
                  name="qa-invoice-delete"
                  color="light"
                  className="p-0 px-1 bg-transparent border-0 float-right"
                  onClick={() => onDelete(invoice.id)}
                >
                  <GoTrashcan />
                </DeleteWithConfirmation>
              )}
          </Col>
        </Row>
        <RequestedChanges invoice={invoice} className="py-1 small" />
      </div>

      <Collapse
        isOpen={expanded}
        className="inv-details-form"
        data-qa-field={`invoice-${invoice.id}-entries`}
      >
        {!isEmpty(invoice.bookingEntries) && (
          <ListBookingEntries
            data={invoice.bookingEntries}
            products={products}
            showUpdateButton={!readOnly}
            invoice={invoice}
            isEditing={isEditing}
          />
        )}
      </Collapse>
    </>
  );
}

InvoiceLine.propTypes = {
  invoice: detailedInvoiceShape.isRequired,
  products: arrayOf(productShape.isRequired).isRequired,
  onDelete: func.isRequired,
  readOnly: bool,
  isOpenByDefault: bool,
  isEditing: bool,
};

InvoiceLine.defaultProps = {
  readOnly: false,
  isOpenByDefault: false,
  isEditing: true,
};

export default InvoiceLine;
